import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from '@material-ui/core/styles';

import IconButton from '@material-ui/core/IconButton';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';

import "@fontsource/fira-mono"; // Defaults to 400 weight
import "@fontsource/fira-mono/700.css"; // For bold text

//////////////////////////////////////////////////////////////////////

const styles = theme => ({
  frame: {
    backgroundColor: theme.palette.background.paper,
    display: "flex",
    flexDirection: "column", // Change for vertical layout
  },
  handle: {
    height: '5px',
    cursor: 'ns-resize',
    backgroundColor: 'transparent',
  },
  innerFrame: {
    display: "flex",
  },
text: {
  flex: 1,
  margin: 0,
  padding: "0 0.25em",
  border: "none",
  outline: "none",
  verticalAlign: "middle",
  resize: "none",
  minHeight: "2em",
  height: "100px",
  lineHeight: "1.2em",
  boxSizing: "border-box",
  fontFamily: "'Fira Mono', monospace",
  fontWeight: "normal",
  fontSize: "14pt",
  [theme.breakpoints.up('md')]: {
    //height: "6em",
  },
},
  buttons: {
    display: "flex",
    flexFlow: "row wrap",
    justifyContent: "center",
    alignContent: "center",
    [theme.breakpoints.up('md')]: {
      flexFlow: "column wrap",
    },
  },
  smallIcons: {
    padding: 0,
  },
});

//////////////////////////////////////////////////////////////////////

class Input extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      height: 48, // Initial height in px (~3em)
    };
    this.input = React.createRef();
    this.resizing = false;
    this.startY = 0;
    this.startHeight = 0;
  }

  componentDidMount() {
    window.client.react.input = this;
    window.client.initInput(this.input.current);
    this.input.current.addEventListener('keydown', this.handleKeyDown);

    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.stopResizing);
  }

  componentWillUnmount() {
    if (this.input.current) {
      this.input.current.removeEventListener('keydown', this.handleKeyDown);
    }

    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.stopResizing);
  }

  recallBack = () => {
    window.client.input.cycleBackward();
    window.client.input.focus(true);
  };

  recallForward = () => {
    window.client.input.cycleForward();
    window.client.input.focus(true);
  };

  handleKeyDown = (e) => {
    if (e.ctrlKey && e.key === 'ArrowUp') {
      e.preventDefault();
      this.recallBack();
    } else if (e.ctrlKey && e.key === 'ArrowDown') {
      e.preventDefault();
      this.recallForward();
    } else if (!e.ctrlKey && e.key === 'ArrowUp') {
      e.preventDefault();
      this.recallBack();
    } else if (!e.ctrlKey && e.key === 'ArrowDown') {
      e.preventDefault();
      this.recallForward();
    }
  };

  startResizing = (e) => {
    this.resizing = true;
    this.startY = e.clientY;
    this.startHeight = this.state.height;
    e.preventDefault();
  };

  handleMouseMove = (e) => {
    if (!this.resizing) return;
    const delta = this.startY - e.clientY;
    this.setState({ height: Math.max(24, this.startHeight + delta) }); // Min height = 24px (~1.5em)
  };

  stopResizing = () => {
    this.resizing = false;
  };

  render() {
    const { classes, ansiFG, ansiBG } = this.props;
    const { fontFamily, fontSize, recallButtons, recallAnchor } = window.client.settings;

    const font = {
      fontFamily: `'${fontFamily}', monospace`,
      fontSize: (window.client.mobile ? 16 : fontSize) + "pt",
      height: this.state.height + 'px',
    };

    const buttons = (
      <span className={classes.buttons}>
        <IconButton classes={{ root: classes.smallIcons }} onClick={this.recallBack}>
          <ArrowDropUpIcon />
        </IconButton>
        <IconButton classes={{ root: classes.smallIcons }} onClick={this.recallForward}>
          <ArrowDropDownIcon />
        </IconButton>
      </span>
    );

    return (
      <div className={classes.frame}>
        <div className={classes.handle} onMouseDown={this.startResizing} />
        <div className={classes.innerFrame}>
          {recallButtons && recallAnchor === "left" && buttons}
          <textarea
            ref={this.input}
            className={classNames(classes.text, ansiFG, ansiBG)}
            style={font}
            autoComplete="off"
            autoFocus={!window.client.mobile}
          />
          {recallButtons && recallAnchor === "right" && buttons}
        </div>
      </div>
    );
  }
}

Input.propTypes = {
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,
  ansiFG: PropTypes.string.isRequired,
  ansiBG: PropTypes.string.isRequired,
};

export default withStyles(styles, { withTheme: true })(Input);